import { API, MessageLoggingPolicyConfiguration } from '../../../remote-api/models'
import { LogPointStates } from './LogPointsService'

export enum LogPointType {
  Request = 'Request',
  Response = 'Response'
}

export enum LogPointCategory {
  Header = 'Header',
  Body = 'Body'
}

export type LoggingPolicyConfigurations = {
  [key: string]: {
    type: LogPointType
    category: LogPointCategory
    label: string
    config: MessageLoggingPolicyConfiguration
  }
}

export const supportedConfigurations: LoggingPolicyConfigurations = {
  ['anypoint-monitoring-log-points-request-user-agent']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'User Agent',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - User Agent',
      itemData: {
        category: 'anypoint-monitoring-log-points-request-user-agent',
        message: `#['Anypoint Monitoring - Log Points - Request - User Agent: ' + message.inboundProperties['user-agent']]`,
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-path']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'Path',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Path',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Path: ' + message.inboundProperties['http.request.path']]`,
        category: 'anypoint-monitoring-log-points-request-path',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-host']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'Host',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Host',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Host: ' + message.inboundProperties['host']]`,
        category: 'anypoint-monitoring-log-points-request-host',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-method']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'Method',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Method',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Method: ' + message.inboundProperties['http.method']]`,
        category: 'anypoint-monitoring-log-points-request-method',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-remote-address']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'Remote Address',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Remote Address',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Remote Address: ' + message.inboundProperties['http.remote.address']]`,
        category: 'anypoint-monitoring-log-points-request-remote-address',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-accept']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'Accept',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Accept',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Accept: ' + message.inboundProperties['accept']]`,
        category: 'anypoint-monitoring-log-points-request-accept',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-accept-encoding']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'Accept Encoding',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Accept Encoding',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Accept Encoding: ' + message.inboundProperties['accept-encoding']]`,
        category: 'anypoint-monitoring-log-points-request-accept-encoding',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-accept-language']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'Accept Language',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Accept Language',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Accept Language: ' + message.inboundProperties['accept-language']]`,
        category: 'anypoint-monitoring-log-points-request-accept-language',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-cookie']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'Cookie',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Cookie',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Cookie: ' + message.inboundProperties['cookie']]`,
        category: 'anypoint-monitoring-log-points-request-cookie',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-referer']: {
    type: LogPointType.Request,
    category: LogPointCategory.Header,
    label: 'Referrer',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Referer',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Referer: ' + message.inboundProperties['referer']]`,
        category: 'anypoint-monitoring-log-points-request-referer',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-request-body']: {
    type: LogPointType.Request,
    category: LogPointCategory.Body,
    label: 'Request Body',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Request - Body',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Request - Body: ' + message.payload]`,
        category: 'anypoint-monitoring-log-points-request-body',
        firstSection: true,
        secondSection: false,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-response-status-code']: {
    type: LogPointType.Response,
    category: LogPointCategory.Header,
    label: 'Status Code',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Response - Status Code',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Response - Status Code: ' + message.outboundProperties['http.status']]`,
        category: 'anypoint-monitoring-log-points-response-status-code',
        firstSection: false,
        secondSection: true,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-response-connection']: {
    type: LogPointType.Response,
    category: LogPointCategory.Header,
    label: 'Connection',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Response - Connection',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Response - Connection: ' + message.outboundProperties['connection']]`,
        category: 'anypoint-monitoring-log-points-response-connection',
        firstSection: false,
        secondSection: true,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-response-date']: {
    type: LogPointType.Response,
    category: LogPointCategory.Header,
    label: 'Date',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Response - Date',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Response - Date: ' + message.outboundProperties['date']]`,
        category: 'anypoint-monitoring-log-points-response-date',
        firstSection: false,
        secondSection: true,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-response-set-cookie']: {
    type: LogPointType.Response,
    category: LogPointCategory.Header,
    label: 'Set-Cookie',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Response - Set-Cookie',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Response - Set-Cookie: ' + message.outboundProperties['set-cookie']]`,
        category: 'anypoint-monitoring-log-points-response-set-cookie',
        firstSection: false,
        secondSection: true,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-response-pragma']: {
    type: LogPointType.Response,
    category: LogPointCategory.Header,
    label: 'Pragma',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Response - Pragma',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Response - Pragma: ' + message.outboundProperties['pragma']]`,
        category: 'anypoint-monitoring-log-points-response-pragma',
        firstSection: false,
        secondSection: true,
        level: 'INFO'
      }
    }
  },
  ['anypoint-monitoring-log-points-response-body']: {
    type: LogPointType.Response,
    category: LogPointCategory.Body,
    label: 'Response Body',
    config: {
      itemName: 'Anypoint Monitoring - Log Points - Response - Body',
      itemData: {
        message: `#['Anypoint Monitoring - Log Points - Response - Body: ' + message.payload]`,
        category: 'anypoint-monitoring-log-points-response-body',
        firstSection: false,
        secondSection: true,
        level: 'INFO'
      }
    }
  }
}

export const supportedConfigurationsKeys: string[] = Object.keys(supportedConfigurations)

export const filterConfiguration = (state: LogPointStates, logPointCategory: LogPointCategory, logPointType: LogPointType) => {
  return (key: string) => state[key].category === logPointCategory && state[key].type === logPointType
}

export const getApplicationName = (versions: API[], selectedVersionId: string) => {
  const selectedVersion = versions.find(r => r.id.toString() === selectedVersionId)
  return selectedVersion ? selectedVersion.applicationName : undefined
}
